import { useEffect } from 'react';

const SITE_NAME = 'QED AI';

/**
 * Keeps the browser tab's title in step with the route.
 *
 * A single-page app changes the view without reloading, so `document.title`
 * keeps whatever the served HTML set — for the whole session. Land on the home
 * page, click into an article, and the tab still says "QED AI"; only someone
 * who opened the article's URL directly ever sees its real title.
 *
 * That is mostly cosmetic in the tab strip and squarely a bug when sharing:
 * a browser's share sheet sends `document.title` alongside the URL, so
 * sharing from inside an article sent the home page's title. Pasting the same
 * link worked, because a link preview is fetched fresh by a crawler and never
 * consults the DOM at all — which is exactly why the two behaved differently.
 *
 * Deliberately does not touch the `og:` tags. Those exist for crawlers, which
 * do not run JavaScript, so editing them at runtime achieves nothing; the
 * prerendered per-article pages are what serve them.
 */
export function useDocumentTitle(title: string | null | undefined): void {
  useEffect(() => {
    if (!title) return;
    // Suffixed to match the prerendered pages, so the title does not change
    // shape depending on how the reader arrived.
    document.title = title === SITE_NAME ? title : `${title} — ${SITE_NAME}`;
  }, [title]);
}
